import React from 'react';
import { useNavigate } from 'react-router-dom';
import './SmartStopLoss.css';

const IpoBullGuide: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="article-page">
      {/* Back Navigation */}
      <div className="article-nav">
        <button className="back-button" onClick={() => navigate('/learning-hub')}>
          ← Back to Learning Hub
        </button>
      </div>

      {/* Article Header */}
      <header className="article-header">
        <div className="article-badge">IPO STRATEGY</div>
        <h1 className="article-title">How to Trade IPO-Based Bullish Patterns</h1>
        <div className="article-meta">
          <span className="meta-item">📅 Updated December 2024</span>
          <span className="meta-item">⏱️ 9 min read</span>
          <span className="meta-item">📊 Advanced Level</span>
        </div>
      </header>

      {/* Article Content */}
      <article className="article-content">

        {/* Introduction */}
        <section className="content-section">
        <div className="ai-badge-container">
          <span className="ai-badge">📊 Pattern Detected by Screener</span>
          <span className="market-badge">🎯 Best for New Listings</span>
        </div>

        <p className="intro-text">
          IPO-based bullish patterns represent one of the most powerful momentum opportunities in the stock market.
          When a newly listed company establishes its base and starts breaking out, it can generate extraordinary returns.
          However, trading IPOs requires understanding specific lifecycle stages, lock-up periods, and institutional accumulation patterns.
        </p>

        <div className="key-insight">
          <div className="insight-icon">💡</div>
          <div className="insight-content">
            <h4>Why IPO Bases Work</h4>
            <p>Studies show that stocks forming their first base after IPO have a 68% success rate when breaking out with proper volume and fundamentals. The key is catching institutional accumulation before the major move.</p>
          </div>
        </div>
      </section>

      {/* What is IPO-Based Bullish Pattern */}
      <section className="content-section">
        <h2>What is an IPO-Based Bullish Pattern?</h2>

        <p>
          An IPO-based bullish pattern occurs when a newly public company (typically within 3-24 months of listing)
          forms a proper base and breaks out to new highs. This is different from random IPO momentum - we're looking
          for structured accumulation phases followed by institutional buying.
        </p>

        <div className="video-placeholder">
          <div className="placeholder-content">
            <div className="play-icon">▶</div>
            <p>Animation: IPO Lifecycle from Listing to Breakout Base</p>
          </div>
        </div>

        <div className="highlight-box">
          <h4>Three Critical IPO Lifecycle Stages</h4>
          <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
            <li style={{ marginBottom: '8px' }}><strong>Stage 1 (Days 1-30):</strong> Initial volatility - Usually avoid trading here. Let price settle and find equilibrium.</li>
            <li style={{ marginBottom: '8px' }}><strong>Stage 2 (Months 1-6):</strong> First base formation - This is where institutional accumulation begins. Watch for tight consolidation.</li>
            <li style={{ marginBottom: '8px' }}><strong>Stage 3 (Months 6-24):</strong> Breakout potential - Stock establishes clear base and breaks to new highs with volume.</li>
          </ul>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">73%</div>
            <div className="stat-label">IPOs with profitable Q1</div>
            <div className="stat-description">Have 2.3x higher success rate in forming bullish bases</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">180 days</div>
            <div className="stat-label">Typical lock-up period</div>
            <div className="stat-description">Watch for volatility before and after expiration</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">3-5 weeks</div>
            <div className="stat-label">Ideal base duration</div>
            <div className="stat-description">Sufficient time for institutional accumulation</div>
          </div>
        </div>
      </section>

      {/* How to Identify */}
      <section className="content-section">
        <h2>How to Identify IPO-Based Bullish Setups</h2>

        <div className="criteria-list">
          <div className="criteria-item">
            <div className="criteria-number">1</div>
            <div className="criteria-content">
              <h4>Listing Age: 3-24 Months</h4>
              <p>Too early (under 3 months) means excessive volatility. Too late (over 24 months) loses the "IPO base" advantage. Sweet spot is 4-12 months post-listing.</p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-number">2</div>
            <div className="criteria-content">
              <h4>Proper Base Formation</h4>
              <p>Our screener identifies 3-8 week consolidations near highs with declining volume. Bases are tight consolidations - not wild swings. Depth filtered to 15-30% maximum.</p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-number">3</div>
            <div className="criteria-content">
              <h4>Strong Post-IPO Performance</h4>
              <p>The screener filters for stocks trading above IPO price OR showing clear bottoming structure if below. IPOs that collapsed 50%+ without recovery are automatically excluded.</p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-number">4</div>
            <div className="criteria-content">
              <h4>Institutional Accumulation Signs</h4>
              <p>Our screener analyzes delivery percentage (above 50%), price holding on down days, and volume patterns during consolidation. This confirms institutions are holding, not dumping.</p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-number">5</div>
            <div className="criteria-content">
              <h4>Fundamental Quality</h4>
              <p>The screener checks quarterly results and filters for strong revenue growth (20%+). IPOs with declining margins or weak fundamentals are automatically filtered out.</p>
            </div>
          </div>

          <div className="criteria-item">
            <div className="criteria-number">6</div>
            <div className="criteria-content">
              <h4>Lock-Up Period Awareness</h4>
              <p>Know when lock-up expires. Best setups occur 2-4 weeks AFTER lock-up expiration when selling pressure has been absorbed and stock stabilizes.</p>
            </div>
          </div>
        </div>

        <div className="video-placeholder">
          <div className="placeholder-content">
            <div className="play-icon">▶</div>
            <p>Chart Example: Perfect IPO Base with All 6 Criteria</p>
          </div>
        </div>
      </section>

      {/* Entry Strategies */}
      <section className="content-section">
        <h2>Entry Strategies for IPO Breakouts</h2>

        <div className="trailing-rules">
          <div className="rule-card">
            <div className="rule-number">Strategy 1</div>
            <h4>Breakout Entry (Most Common)</h4>
            <p>Enter when stock breaks above base resistance with 2x average volume. Wait for 1-hour confirmation to avoid false breakouts.</p>
            <div className="example-calc">
              <p><strong>Setup:</strong> Stock consolidates 3-7 weeks between ₹850-₹900</p>
              <p><strong>Trigger:</strong> Breaks ₹900 on heavy volume</p>
              <p><strong>Entry:</strong> ₹905 (small cushion above resistance)</p>
              <p className="result">✅ Clear breakout with institutional support</p>
            </div>
          </div>

          <div className="rule-card">
            <div className="rule-number">Strategy 2</div>
            <h4>Post Lock-Up Entry</h4>
            <p>Wait for lock-up expiration selling to complete, then enter when stock stabilizes and starts moving up. This avoids the initial dump but catches the recovery.</p>
            <div className="example-calc">
              <p><strong>Lock-up expires:</strong> January 15</p>
              <p><strong>Stock drops:</strong> ₹1200 → ₹1050 over 5 days</p>
              <p><strong>Stabilization:</strong> Holds ₹1050-₹1080 for 1 week</p>
              <p><strong>Entry:</strong> When breaks ₹1080 with volume</p>
              <p className="result">✅ Selling pressure absorbed, now accumulation phase</p>
            </div>
          </div>

          <div className="rule-card">
            <div className="rule-number">Strategy 3</div>
            <h4>Pullback Entry (Lower Risk)</h4>
            <p>After initial breakout, wait for pullback to breakout level or 9 EMA. Enter when support holds and uptrend resumes.</p>
            <div className="example-calc">
              <p><strong>Initial Breakout:</strong> ₹900 → ₹980</p>
              <p><strong>Pullback:</strong> Retraces to ₹915 (just above ₹900 breakout)</p>
              <p><strong>Entry:</strong> ₹920 when shows reversal signal</p>
              <p className="result">✅ Lower risk entry with defined stop</p>
            </div>
          </div>
        </div>

        <div className="video-placeholder">
          <div className="placeholder-content">
            <div className="play-icon">▶</div>
            <p>Animation: Three Entry Strategies Compared</p>
          </div>
        </div>
      </section>

      {/* Profit Targets */}
      <section className="content-section">
        <h2>Profit Targets and Exit Strategy</h2>

        <div className="highlight-box">
          <h4>IPO Breakout Target Zones</h4>
          <ul style={{ margin: '12px 0', paddingLeft: '20px' }}>
            <li style={{ marginBottom: '8px' }}><strong>Conservative Target:</strong> 15-20% gain (1.5-2x your risk). Book 1/3 position here.</li>
            <li style={{ marginBottom: '8px' }}><strong>Standard Target:</strong> 25-35% gain (3-4x your risk). Book another 1/3 position here.</li>
            <li style={{ marginBottom: '8px' }}><strong>Extended Target:</strong> 50%+ gain. Let remaining 1/3 run with trailing stop.</li>
          </ul>
        </div>

        <div className="trailing-rules">
          <div className="rule-card">
            <div className="rule-number">Exit Rule 1</div>
            <h4>Partial Profit Taking</h4>
            <p>Scale out as stock moves up. Never sell everything at first target - IPO breakouts can run much further than expected when fundamentals support the move.</p>
          </div>

          <div className="rule-card">
            <div className="rule-number">Exit Rule 2</div>
            <h4>Trail with 9 EMA</h4>
            <p>Once stock is up 20%+, use 9 EMA as trailing stop. Exit remaining position when stock closes below 9 EMA with high volume.</p>
          </div>

          <div className="rule-card">
            <div className="rule-number">Exit Rule 3</div>
            <h4>Watch for Exhaustion Signs</h4>
            <p>Exit if you see: parabolic move up (unsustainable), extremely high volume climax, negative divergence on RSI, or bearish reversal candles at resistance.</p>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="content-section">
        <h2>Common Mistakes to Avoid</h2>

        <div className="mistakes-grid">
          <div className="mistake-card">
            <div className="mistake-icon">❌</div>
            <h4>Trading Too Early After IPO</h4>
            <p>Jumping into IPO on day 1-2 without letting it establish base. First 2-4 weeks are typically wild and unpredictable.</p>
            <div className="mistake-solution">
              <strong>Solution:</strong> Wait minimum 3 months, ideally 6+ months for first proper base formation.
            </div>
          </div>

          <div className="mistake-card">
            <div className="mistake-icon">❌</div>
            <h4>Ignoring Lock-Up Period</h4>
            <p>Entering right before lock-up expiration. Insiders and early investors can dump shares, causing 20-40% drops.</p>
            <div className="mistake-solution">
              <strong>Solution:</strong> Check lock-up date. Enter 2+ weeks AFTER expiration when selling pressure is absorbed.
            </div>
          </div>

          <div className="mistake-card">
            <div className="mistake-icon">❌</div>
            <h4>Chasing Weak Fundamentals</h4>
            <p>Trading IPO chart patterns when company has no earnings, declining revenue, or weak business model.</p>
            <div className="mistake-solution">
              <strong>Solution:</strong> Verify strong revenue growth (20%+), improving margins, and solid industry positioning.
            </div>
          </div>

          <div className="mistake-card">
            <div className="mistake-icon">❌</div>
            <h4>Using Same Position Size</h4>
            <p>Taking full position size in IPO stock same as established stocks. IPOs have higher volatility and failure rate.</p>
            <div className="mistake-solution">
              <strong>Solution:</strong> Use 50-70% of normal position size. Compensate with wider stops while maintaining same rupee risk.
            </div>
          </div>

          <div className="mistake-card">
            <div className="mistake-icon">❌</div>
            <h4>Buying IPOs Already Up 100%+</h4>
            <p>Entering after massive run from IPO price. These often pullback hard as early investors book profits.</p>
            <div className="mistake-solution">
              <strong>Solution:</strong> Focus on IPOs within 30-50% of IPO price or forming first consolidation after initial move.
            </div>
          </div>

          <div className="mistake-card">
            <div className="mistake-icon">❌</div>
            <h4>Holding Through Earnings</h4>
            <p>Holding IPO stock through first 2-3 earnings reports. These can be extremely volatile as market adjusts expectations.</p>
            <div className="mistake-solution">
              <strong>Solution:</strong> Reduce or exit position before earnings. Re-enter after results if setup remains intact.
            </div>
          </div>
        </div>
      </section>

      {/* Real Example */}
      <section className="content-section">
        <h2>Real Trading Example</h2>

        <div className="example-box">
          <h3>Zomato Ltd (ZOMATO) - Post-IPO Breakout (2022)</h3>

          <div className="example-timeline">
            <div className="timeline-item">
              <div className="timeline-date">July 2021</div>
              <div className="timeline-content">
                <strong>IPO Launch:</strong> ₹76 per share. Initial pop to ₹125, then correction.
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">Aug-Nov 2021</div>
              <div className="timeline-content">
                <strong>Volatility Phase:</strong> Stock swings between ₹85-₹150. Too early to trade.
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">Dec 2021 - Feb 2022</div>
              <div className="timeline-content">
                <strong>Base Formation:</strong> Consolidates 8 weeks between ₹50-₹65. Volume declining - accumulation phase.
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">March 2022</div>
              <div className="timeline-content">
                <strong>Breakout:</strong> Breaks ₹65 on earnings beat. Volume 3x average. Entry: ₹67. Stop: ₹59 (8% risk).
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">March - April 2022</div>
              <div className="timeline-content">
                <strong>Move:</strong> Runs to ₹98 in 5 weeks (+46% gain). Book 1/3 at ₹85 (+27%), 1/3 at ₹95 (+42%), trail final 1/3.
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-date">Result</div>
              <div className="timeline-content">
                <strong>Average Exit:</strong> ₹90 (+34% average gain). Risk was 8%, reward was 4.25x the risk.
              </div>
            </div>
          </div>

          <div className="video-placeholder" style={{ marginTop: '24px' }}>
            <div className="placeholder-content">
              <div className="play-icon">▶</div>
              <p>Chart Replay: ZOMATO IPO Base Breakout Setup</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Reference */}
      <section className="content-section">
        <h2>Quick Reference Table</h2>

        <div className="quick-reference-table">
          <table>
            <thead>
              <tr>
                <th>Aspect</th>
                <th>Criteria</th>
                <th>Why It Matters</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>IPO Age</strong></td>
                <td>3-24 months old</td>
                <td>Sweet spot for first base formation</td>
              </tr>
              <tr>
                <td><strong>Base Duration</strong></td>
                <td>3-8 weeks</td>
                <td>Sufficient institutional accumulation time</td>
              </tr>
              <tr>
                <td><strong>Base Depth</strong></td>
                <td>15-30% max</td>
                <td>Tight bases show strength</td>
              </tr>
              <tr>
                <td><strong>Volume Pattern</strong></td>
                <td>Declining in base, surging on breakout</td>
                <td>Confirms accumulation → distribution shift</td>
              </tr>
              <tr>
                <td><strong>Revenue Growth</strong></td>
                <td>20%+ YoY</td>
                <td>Fundamental support for price move</td>
              </tr>
              <tr>
                <td><strong>Lock-Up Period</strong></td>
                <td>Trade 2+ weeks after expiration</td>
                <td>Avoid insider selling pressure</td>
              </tr>
              <tr>
                <td><strong>Entry</strong></td>
                <td>Breakout with 2x volume</td>
                <td>Institutional participation confirmation</td>
              </tr>
              <tr>
                <td><strong>Position Size</strong></td>
                <td>50-70% of normal size</td>
                <td>Higher volatility = smaller position</td>
              </tr>
              <tr>
                <td><strong>Target</strong></td>
                <td>25-50%+ (scale out)</td>
                <td>IPO breakouts can extend far</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Summary */}
      <section className="content-section">
        <h2>Key Takeaways</h2>

        <div className="takeaways-grid">
          <div className="takeaway-card">
            <div className="takeaway-number">1</div>
            <p>IPO-based bullish patterns offer exceptional reward-to-risk when traded properly. Focus on 4-12 month old IPOs with strong fundamentals.</p>
          </div>
          <div className="takeaway-card">
            <div className="takeaway-number">2</div>
            <p>Always check lock-up period. Enter 2+ weeks AFTER expiration when selling pressure has been absorbed and stock stabilizes.</p>
          </div>
          <div className="takeaway-card">
            <div className="takeaway-number">3</div>
            <p>Use wider stops (7-10%) but reduce position size to maintain same rupee risk. IPOs are more volatile than established stocks.</p>
          </div>
          <div className="takeaway-card">
            <div className="takeaway-number">4</div>
            <p>Scale out profits - take 1/3 at conservative target, 1/3 at standard target, trail final 1/3. IPO breakouts can run 50%+.</p>
          </div>
          <div className="takeaway-card">
            <div className="takeaway-number">5</div>
            <p>Combine technical setup with fundamental quality. Verify 20%+ revenue growth and improving margins before entering any IPO trade.</p>
          </div>
        </div>

        <div className="final-tip">
          <h4>🎯 Pro Tip for IPO Trading</h4>
          <p>
            Our IPO-Based Bullish Screener automatically tracks ALL IPOs from the past 6-18 months and identifies those forming proper base patterns.
            The screener specializes in finding "second chance" setups - companies that initially disappointed but are now showing strong earnings turnaround.
            These setups have less hype and better risk-reward. Let our screener do the heavy lifting of monitoring and finding these opportunities for you!
          </p>
        </div>
      </section>

      </article>

      {/* Related Articles */}
      <section className="related-articles">
        <h3>Continue Learning</h3>
        <div className="related-grid">
          <div className="related-card" onClick={() => navigate('/learning-hub/vcp-guide')}>
            <div className="related-icon">📈</div>
            <h4>VCP Pattern Guide</h4>
            <p>Master volatility contraction patterns for high-probability breakouts</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/smart-stoploss')}>
            <div className="related-icon">🎯</div>
            <h4>Smart Stop-Loss</h4>
            <p>AI-powered stop-loss techniques to protect profits and cut losses</p>
          </div>
          <div className="related-card" onClick={() => navigate('/learning-hub/gap-up-guide')}>
            <div className="related-icon">⚡</div>
            <h4>Gap Up Trading</h4>
            <p>Capitalize on morning gaps with proper entry and risk management</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="article-cta">
        <h3>Ready to Find IPO-Based Bullish Setups?</h3>
        <p>Our IPO-Based Bullish Screener automatically scans all recent IPOs and identifies those forming proper bases with institutional accumulation signs.</p>
        <button className="cta-button" onClick={() => navigate('/screener/ipo-based-bullish')}>
          Try IPO-Based Bullish Screener →
        </button>
      </section>
    </div>
  );
};

export default IpoBullGuide;
